import React, { useState, useEffect, useCallback, memo } from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CarouselSlides from "./CarouselSlides";
import { useScreenSize } from "../../_context/ScreenSizeProvider";
import PaginationDots from "./PaginationDots";

// Styled Components
const CarouselContainer = styled(Box)(({ theme ,width}) => ({
  position: "relative",
  width: `calc(${width}px - 4rem)`,
  margin: "0px auto",
  cursor: "pointer",
  flexDirection: "column",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height:"100%"
}));

const CustomerCarousel = ({ data = [], CardComponent = null }) => {
  if (!Array.isArray(data) || data.length <= 0) return;
  const theme = useTheme();

  const { isResizing, width } = useScreenSize();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));

  const getVisibleItems = () => {
    if (isXs) return 1;
    if (isSm) return 2;
    if (isMd) return 3;
    if (isLg) return 4;
    return 3;
  };

  const visibleItems = getVisibleItems();
  const totalItems = data.length;

  // State variables
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isHovered, setIsHovered] = useState(false);
  const [itemwidthpx, setitemwidthpx] = useState(300);
  const [currentBlock, setCurrentBlock] = useState([]);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  /**
   * Calculate the width of each carousel item based on the container width and number of visible items.
   */
  useEffect(() => {
    const containerWidth = visibleItems === 1 ? width * 0.7 : width * 0.85; // Adjust as needed
    const calculatedItemWidth = containerWidth / visibleItems;
    setitemwidthpx(calculatedItemWidth);
  }, [isResizing, width, visibleItems]);

  /**
   * Update the current block of items to display based on the current index and number of visible items.
   */
  useEffect(() => {
    const newBlock = Array.from(
      { length: visibleItems },
      (_, i) => data[(currentIndex + i) % totalItems]
    );
    setCurrentBlock(newBlock);
  }, [currentIndex, visibleItems, totalItems, data]);

  /**
   * Handle moving to the next set of items.
   */
  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + visibleItems) % totalItems);
    setDirection(1);
  }, [totalItems, visibleItems]);

  /**
   * Handle moving to the previous set of items.
   */
  const handlePrev = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex - visibleItems;
      return newIndex < 0 ? (totalItems + newIndex) % totalItems : newIndex;
    });
    setDirection(-1);
  }, [visibleItems, totalItems]);

  /**
   * Autoplay functionality: Automatically move to the next set of items every 5 seconds.
   * Pause autoplay when the carousel is hovered.
   */
  useEffect(() => {
    if (isHovered) return; // Do not autoplay when hovered

    const autoplay = setInterval(() => {
      handleNext();
    }, 5000); // Change slide every 5 seconds

    // Cleanup interval on component unmount or when dependencies change
    return () => clearInterval(autoplay);
  }, [handleNext, isHovered]);

  /**
   * Handle touch start event: Capture the initial X coordinate.
   */
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  /**
   * Handle touch move event: Update the touch end position.
   */
  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
  };

  /**
   * Handle touch end event: Detect swipe direction and trigger carousel move.
   */
  const handleTouchEnd = () => {
    if (touchStartX - touchEndX > 100) {
      // Swipe left: go to next slide
      handleNext();
    }

    if (touchStartX - touchEndX < -100) {
      // Swipe right: go to previous slide
      handlePrev();
    }

    // Reset touch positions
    setTouchStartX(0);
    setTouchEndX(0);
  };

  // Calculate pagination details
  const totalPages = Math.ceil(totalItems / visibleItems);
  const currentPage = Math.floor(currentIndex / visibleItems);
  

  return (
    <CarouselContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      width={width}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          

        }}
      >
        {/* Previous Button */}
        <IconButton
          onClick={handlePrev}
          color="primary"
          aria-label="Previous"
          sx={{
            mr: 1,
            backgroundColor: "transparent",
            color: "primary.main",
            "&:hover": {
              backgroundColor: "primary.main", // Change background on hover
              color: "white.main", // Change text color on hover
            },
            transition: "background-color 0.3s, color 0.3s", // Smooth transition
          }}
        >
          <ArrowBackIosNewIcon style={{ padding: 0, margin: 0 }} />
        </IconButton>

        {/* Carousel Slides */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CarouselSlides
            direction={direction}
            currentBlock={currentBlock}
            itemwidthpx={itemwidthpx}
            CardComponent={CardComponent}
          />
        </Box>

        {/* Next Button */}
        <IconButton
          onClick={handleNext}
          color="primary"
          aria-label="Next"
          sx={{
            ml: 1,
            backgroundColor: "transparent",
            color: "primary.main",
            "&:hover": {
              backgroundColor: "primary.main", // Change background on hover
              color: "white.main", // Change text color on hover
            },
            transition: "background-color 0.3s, color 0.3s", // Smooth transition
          }}
        >
          <ArrowForwardIosIcon style={{ padding: 0, margin: 0 }} />
        </IconButton>
      </Box>

      {/* Pagination Dots */}
      <PaginationDots
        totalPages={totalPages}
        currentPage={currentPage}
        onDotClick={(page) => setCurrentIndex(page * visibleItems)}
      />
    </CarouselContainer>
  );
};

export default memo(CustomerCarousel);
